/**
 * Datas civis (calendar dates), não instantes.
 *
 * Uma viagem começa e termina em DIAS, não em momentos. Tratar `start_date` e
 * `end_date` como `Date` (instante UTC) é a origem clássica do bug de "um dia
 * a menos" quando o dispositivo está num fuso a oeste do servidor.
 *
 * Por isso:
 *   - datas de viagem são strings `YYYY-MM-DD`;
 *   - duração, número do dia e calendário são aritmética inteira de dias;
 *   - o fuso da viagem só é necessário para responder "que dia é hoje?".
 *
 * Consequência: nenhuma função aqui depende do fuso do dispositivo.
 */

/** Data civil no formato `YYYY-MM-DD`. */
export type CalendarDate = string & { readonly __brand: 'CalendarDate' };

const PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const MS_PER_DAY = 86_400_000;

export class InvalidCalendarDateError extends Error {
  constructor(value: string) {
    super(`Data civil inválida: "${value}". Formato esperado: YYYY-MM-DD.`);
    this.name = 'InvalidCalendarDateError';
  }
}

/** Valida e converte uma string em `CalendarDate`. Rejeita datas inexistentes. */
export function calendarDate(value: string): CalendarDate {
  const match = PATTERN.exec(value);
  if (!match) throw new InvalidCalendarDateError(value);

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  // Round-trip: 2026-02-30 casa com o padrão mas não existe.
  const utc = new Date(Date.UTC(year, month - 1, day));
  if (
    utc.getUTCFullYear() !== year ||
    utc.getUTCMonth() !== month - 1 ||
    utc.getUTCDate() !== day
  ) {
    throw new InvalidCalendarDateError(value);
  }

  return value as CalendarDate;
}

export function isCalendarDate(value: string): value is CalendarDate {
  try {
    calendarDate(value);
    return true;
  } catch {
    return false;
  }
}

/** Dias desde 1970-01-01. Base de toda a aritmética. */
export function toEpochDay(date: CalendarDate): number {
  const [year, month, day] = date.split('-').map(Number) as [number, number, number];
  return Date.UTC(year, month - 1, day) / MS_PER_DAY;
}

export function fromEpochDay(epochDay: number): CalendarDate {
  const utc = new Date(epochDay * MS_PER_DAY);
  const year = String(utc.getUTCFullYear()).padStart(4, '0');
  const month = String(utc.getUTCMonth() + 1).padStart(2, '0');
  const day = String(utc.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}` as CalendarDate;
}

/** `a - b`, em dias inteiros. Positivo quando `a` é posterior. */
export function diffCalendarDays(a: CalendarDate, b: CalendarDate): number {
  return toEpochDay(a) - toEpochDay(b);
}

export function addCalendarDays(date: CalendarDate, days: number): CalendarDate {
  return fromEpochDay(toEpochDay(date) + days);
}

export function compareCalendarDates(a: CalendarDate, b: CalendarDate): number {
  return toEpochDay(a) - toEpochDay(b);
}

/** Intervalo fechado: `start <= date <= end`. */
export function isWithinRange(
  date: CalendarDate,
  start: CalendarDate,
  end: CalendarDate,
): boolean {
  const value = toEpochDay(date);
  return value >= toEpochDay(start) && value <= toEpochDay(end);
}

/**
 * Todos os dias do intervalo fechado `[start, end]`.
 * É o que gera os `itinerary_day` de uma viagem.
 */
export function enumerateDays(start: CalendarDate, end: CalendarDate): CalendarDate[] {
  const first = toEpochDay(start);
  const last = toEpochDay(end);
  if (last < first) return [];

  const days: CalendarDate[] = [];
  for (let d = first; d <= last; d += 1) days.push(fromEpochDay(d));
  return days;
}

/**
 * "Que dia é hoje?" vive em `timezone.ts` — é a única pergunta deste domínio
 * que depende de fuso, e ela precisa da detecção de suporte e do fallback.
 * Deliberadamente não há uma versão ingênua aqui: duas funções, uma delas
 * insegura, seria um convite ao uso errado.
 */
